import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthService, DictionaryService, SettingsService } from '@app/services';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, from, map } from 'rxjs';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { JWTRequestInterceptor } from './core/jwt/jwt-request.interceptor';
import { JWTResponseInterceptor } from './core/jwt/jwt-response.interceptor';
import { HomePageComponent } from './main/homepage/homepage.component';
import { LoginComponent } from './main/auth/login/login.component';
import { SignUpComponent } from './main/auth/sign-up/sign-up.component';
import { MainModule } from './main/main.module';
import { UserManualModule } from './main/user-manual/user-manual.module';
import { SharedModule } from './shared/shared.module';
import { environment } from 'environments/environment';
import { NgOptimizedImage } from '@angular/common';
import { FallbackTranslator } from '@app/classes';

const httpLoaderFactory = (http: HttpClient): TranslateHttpLoader => new TranslateHttpLoader(http, './assets/i18n/', '.json');

function initializeAppFactory(settings: SettingsService, dictionaryService: DictionaryService, translate: TranslateService): () => Observable<any> {
  return () => {
    return from(dictionaryService.getLanguages()).pipe(
      map((languages) => {
        if (!languages) return;
        const languageCode = navigator.language.split('-')[0];
        const selectedLang = languages.findByProp('languageCode', languageCode);
        translate.setDefaultLang('en-US'); // Questo serve per fare un load forzato della lingua inglese, in caso di traduzione mancante
        translate.setDefaultLang(`${selectedLang.languageCode}-${selectedLang.countryCode}`);
        settings.loadLanguage(selectedLang);
      }),
    );
  };
}

@NgModule({
  declarations: [AppComponent, LoginComponent, SignUpComponent, HomePageComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    HttpClientModule,
    AppRoutingModule,
    SharedModule,
    MainModule,
    UserManualModule,
    TranslateModule.forRoot({
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: FallbackTranslator },
      useDefaultLang: false,
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgOptimizedImage,
  ],
  providers: [
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [SettingsService, DictionaryService, TranslateService],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.reCaptchaKey,
    },
    [
      {
        provide: HTTP_INTERCEPTORS,
        useClass: JWTRequestInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: JWTResponseInterceptor,
        multi: true,
      },
    ],
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
